// Ошибка для человека: причина и следующий шаг из ответа сервера, код — отдельно, моноширинным
// (contracts/errors.md; README «Состояния»: без технических подробностей). Обрыв связи —
// код интерфейса NET-001 (contracts/errors.md, «Коды интерфейса»; FR-040, принцип V).
// Ответ сервиса несёт номер операции: «Код SYS-001 · операция 7F3K-92QD-1A2B» и «Скопировать
// номер» (FR-028; 002, contracts/errors.md, «Номер операции»). У NET-001 номера нет. Отказ
// сервера по полям формы: тексты — под полями, код и номер — в выноске формы (T058;
// contracts/ui.md, «Ошибки с номером операции»).
import { clientErrorCatalog, renderError } from '@kp/contracts';
import type { AuthSettings, ClientErrorCode, ErrorCode } from '@kp/contracts';
import { useToast } from '@kp/ui';
import { useCallback } from 'react';
import { ApiError, NetworkError, isApiError } from '../api/client.ts';

export interface ErrorView {
  /** Причина: «Нет доступа к этому действию». */
  message: string;
  /** Следующий шаг: «Обратитесь к администратору клиента: …». */
  nextStep: string;
  /** Код ответа сервиса или код интерфейса (NET-001 — нет связи). */
  code?: ErrorCode | ClientErrorCode;
  /** Номер операции из ответа сервиса; без ответа (нет связи, сбой в браузере) — нет. */
  operation?: string;
}

/** Нет связи — свой текст и «Повторить» (ux.md, «Нет связи»). */
export const OFFLINE_TEXT = 'Изменения не сохранены. Нет связи';

/** Код обрыва связи: запрос не дошёл до сервиса. */
export const NETWORK_ERROR_CODE: ClientErrorCode = 'NET-001';

export function errorView(error: unknown): ErrorView {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      nextStep: error.nextStep,
      code: error.code,
      ...(error.operation ? { operation: error.operation } : {}),
    };
  }
  if (error instanceof NetworkError) {
    return { ...clientErrorCatalog[NETWORK_ERROR_CODE], code: NETWORK_ERROR_CODE };
  }
  const fallback = renderError('SYS-001');
  return { message: fallback.message, nextStep: fallback.nextStep, code: fallback.code };
}

/** Метка каталога осталась незаполненной: ответ пришёл без текстов (сбой прокси). */
const UNFILLED = /\{[^}]*\}/;

/**
 * Неудача входа (вход и окно «Войдите снова»): AUTH-002 — сколько ждать в минутах. Тексты
 * присылает сервер со значениями из настроек. Если ответ пришёл без текстов (сбой прокси),
 * минуты считаются по retryAfter, число попыток — по настройкам сеанса, если они есть; без
 * них причина называется без числа. Без ФИО (FR-016б).
 */
export function loginFailureView(
  error: unknown,
  settings?: Pick<AuthSettings, 'maxFailures'>,
): ErrorView {
  const view = errorView(error);
  if (!isApiError(error, 'AUTH-002') || !UNFILLED.test(view.message + view.nextStep)) {
    return view;
  }
  const seconds = error.body.retryAfter;
  const minutes = seconds === undefined ? undefined : Math.max(1, Math.ceil(seconds / 60));
  if (minutes !== undefined && settings) {
    const rendered = renderError('AUTH-002', { minutes, failures: settings.maxFailures });
    return {
      message: rendered.message,
      nextStep: rendered.nextStep,
      code: rendered.code,
      ...(view.operation ? { operation: view.operation } : {}),
    };
  }
  return {
    // «Вход временно заблокирован» — без числа попыток, которого ответ не принёс.
    message: view.message.replace(/\s*после \{N\}.*$/, ''),
    nextStep:
      minutes === undefined
        ? view.nextStep.replace(/\s*через \{минут\} мин/, ' позже')
        : view.nextStep.replace('{минут}', String(minutes)),
    code: 'AUTH-002',
    ...(view.operation ? { operation: view.operation } : {}),
  };
}

/** «Причина. Следующий шаг». */
export function errorSentence(error: unknown): string {
  const view = errorView(error);
  return `${view.message}. ${view.nextStep}`;
}

// --- Отказ сервера по полям формы (T058) ---

/** Тексты под полями формы: поле → текст ошибки. */
export type FieldTexts<F extends string> = Partial<Record<F, string | undefined>>;

/**
 * Отказ сервера, который форма показала под полями: тексты, поставленные под поля, и ответ —
 * причина, шаг, код и номер операции — для выноски формы (FieldsRefusalCallout).
 */
export interface FieldsRefusal<F extends string> {
  readonly fields: FieldTexts<F>;
  readonly view: ErrorView;
}

/** Есть ли хоть один текст: отказ, не попавший ни в одно поле формы, показывается целиком. */
export function hasFieldTexts<F extends string>(fields: FieldTexts<F>): boolean {
  return Object.values<string | undefined>(fields).some(
    (text) => text !== undefined && text !== '',
  );
}

/**
 * Выноска отказа видна, пока под полями стоит хотя бы один его текст: поле исправили или
 * перепроверили при вводе — текст ушёл, и код с номером операции больше не о чём.
 */
export function shownRefusal<F extends string>(
  refusal: FieldsRefusal<F> | null,
  errors: FieldTexts<F>,
): FieldsRefusal<F> | null {
  if (!refusal) return null;
  const fields = Object.keys(refusal.fields) as F[];
  return fields.some((field) => refusal.fields[field] && errors[field] === refusal.fields[field])
    ? refusal
    : null;
}

/**
 * Причина отказа для выноски: если она уже стоит под полем («Текущий пароль введён неверно.
 * Введите текущий пароль ещё раз»), в выноске её нет — только код и номер операции.
 */
export function refusalLead<F extends string>(
  refusal: FieldsRefusal<F>,
  errors: FieldTexts<F>,
): string | undefined {
  const { message } = refusal.view;
  const shown = Object.values<string | undefined>(errors).some((text) => text?.includes(message));
  return shown ? undefined : `${message}.`;
}

/**
 * Тост-ошибка несохранённого действия: причина, шаг, код и номер операции; без связи —
 * «Изменения не сохранены. Нет связи · Повторить» с кодом NET-001.
 */
export function useErrorToast(): (error: unknown, retry?: () => void) => void {
  const toast = useToast();
  return useCallback(
    (error: unknown, retry?: () => void) => {
      const action = retry ? { label: 'Повторить', onClick: retry } : undefined;
      if (error instanceof NetworkError) {
        toast.error(OFFLINE_TEXT, { action, duration: 'long', code: NETWORK_ERROR_CODE });
        return;
      }
      const view = errorView(error);
      toast.error(`${view.message}. ${view.nextStep}`, {
        code: view.code,
        operation: view.operation,
        action,
        duration: 'long',
      });
    },
    [toast],
  );
}
